import { useState, useCallback, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Table,
  Badge,
  Modal,
  Alert,
  Spinner,
} from "react-bootstrap";
import toast from "react-hot-toast";
import api from "../utils/api";

function SuperAdmin() {
  // ── Store state ────────────────────────────────────────────────────────────
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [storeForm, setStoreForm] = useState({ name: "", address: "", phone: "", email: "" });
  const [editingStoreId, setEditingStoreId] = useState(null);
  const [showStoreModal, setShowStoreModal] = useState(false);

  // ── User state (per selected store) ────────────────────────────────────────
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeUsers, setStoreUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userForm, setUserForm] = useState({ username: "", email: "", password: "", role: "user" });
  const [showUserModal, setShowUserModal] = useState(false);

  // ── Fetch stores ───────────────────────────────────────────────────────────
  const fetchStores = useCallback(async () => {
    setLoadingStores(true);
    try {
      const { data } = await api.get("/api/stores");
      setStores(data.stores || []);
    } catch (err) {
      toast.error("Failed to load stores");
    } finally {
      setLoadingStores(false);
    }
  }, []);

  useEffect(() => {
    fetchStores();
  }, [fetchStores]);

  // ── Store CRUD ─────────────────────────────────────────────────────────────
  const openCreateStore = () => {
    setEditingStoreId(null);
    setStoreForm({ name: "", address: "", phone: "", email: "" });
    setShowStoreModal(true);
  };

  const openEditStore = (store) => {
    setEditingStoreId(store._id);
    setStoreForm({ name: store.name, address: store.address || "", phone: store.phone || "", email: store.email || "" });
    setShowStoreModal(true);
  };

  const saveStore = async () => {
    try {
      if (editingStoreId) {
        await api.put(`/api/stores/${editingStoreId}`, storeForm);
        toast.success("Store updated");
      } else {
        await api.post("/api/stores", storeForm);
        toast.success("Store created");
      }
      setShowStoreModal(false);
      fetchStores();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save store");
    }
  };

  const deleteStore = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? All user associations will be removed.`)) return;
    try {
      await api.delete(`/api/stores/${id}`);
      toast.success("Store deleted");
      if (selectedStore?._id === id) {
        setSelectedStore(null);
        setStoreUsers([]);
      }
      fetchStores();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete store");
    }
  };

  // ── Store users ────────────────────────────────────────────────────────────
  const selectStore = async (store) => {
    setSelectedStore(store);
    setLoadingUsers(true);
    try {
      const { data } = await api.get(`/api/stores/${store._id}/users`);
      setStoreUsers(data.users || []);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const openCreateUser = () => {
    setUserForm({ username: "", email: "", password: "", role: "user" });
    setShowUserModal(true);
  };

  const createUser = async () => {
    try {
      await api.post(`/api/stores/${selectedStore._id}/create-user`, userForm);
      toast.success("User created");
      setShowUserModal(false);
      selectStore(selectedStore); // refresh
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create user");
    }
  };

  const removeUser = async (userId, username) => {
    if (!window.confirm(`Remove "${username}" from ${selectedStore.name}?`)) return;
    try {
      await api.delete(`/api/stores/${selectedStore._id}/users/${userId}`);
      toast.success("User removed from store");
      selectStore(selectedStore); // refresh
    } catch (err) {
      toast.error("Failed to remove user");
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Container fluid className="py-3">
      <h3 className="mb-3">⚙️ Super Admin — Store Management</h3>
      <hr />

      <Row>
        {/* ── Left: Stores list ──────────────────────────────────────────── */}
        <Col md={5}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center bg-dark text-white">
              <span>Stores</span>
              <Button size="sm" variant="success" onClick={openCreateStore}>
                + New Store
              </Button>
            </Card.Header>
            <Card.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {loadingStores ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
              ) : stores.length === 0 ? (
                <Alert variant="info">No stores yet. Create one to get started.</Alert>
              ) : (
                <Table hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stores.map((s) => (
                      <tr
                        key={s._id}
                        className={selectedStore?._id === s._id ? "table-active" : ""}
                        style={{ cursor: "pointer" }}
                        onClick={() => selectStore(s)}
                      >
                        <td>{s.name}</td>
                        <td className="text-muted small">{s.phone || "—"}</td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            className="me-1"
                            onClick={(e) => { e.stopPropagation(); openEditStore(s); }}
                          >
                            ✏️
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={(e) => { e.stopPropagation(); deleteStore(s._id, s.name); }}
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* ── Right: Users for selected store ────────────────────────────── */}
        <Col md={7}>
          {selectedStore ? (
            <Card className="shadow-sm">
              <Card.Header className="d-flex justify-content-between align-items-center bg-dark text-white">
                <span>Users — {selectedStore.name}</span>
                <Button size="sm" variant="success" onClick={openCreateUser}>
                  + Create User
                </Button>
              </Card.Header>
              <Card.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {loadingUsers ? (
                  <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : storeUsers.length === 0 ? (
                  <Alert variant="info">No users assigned to this store yet.</Alert>
                ) : (
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Username</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {storeUsers.map((u) => (
                        <tr key={u._id}>
                          <td>{u.username}</td>
                          <td className="small">{u.email}</td>
                          <td>
                            <Badge bg={u.role === "admin" ? "warning" : "secondary"}>
                              {u.role}
                            </Badge>
                          </td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => removeUser(u._id, u.username)}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          ) : (
            <div className="text-muted text-center mt-5">
              <h5>← Select a store to manage its users</h5>
            </div>
          )}
        </Col>
      </Row>

      {/* ── Store Modal ───────────────────────────────────────────────────── */}
      <Modal show={showStoreModal} onHide={() => setShowStoreModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingStoreId ? "Edit Store" : "Create Store"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Store Name *</Form.Label>
              <Form.Control
                value={storeForm.name}
                onChange={(e) => setStoreForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Downtown Liquors"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Address</Form.Label>
              <Form.Control
                value={storeForm.address}
                onChange={(e) => setStoreForm((f) => ({ ...f, address: e.target.value }))}
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    value={storeForm.phone}
                    onChange={(e) => setStoreForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={storeForm.email}
                    onChange={(e) => setStoreForm((f) => ({ ...f, email: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStoreModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={saveStore} disabled={!storeForm.name.trim()}>
            {editingStoreId ? "Save Changes" : "Create Store"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Create User Modal ─────────────────────────────────────────────── */}
      <Modal show={showUserModal} onHide={() => setShowUserModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create User — {selectedStore?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Username *</Form.Label>
              <Form.Control
                value={userForm.username}
                onChange={(e) => setUserForm((f) => ({ ...f, username: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Password * (min 6 chars)</Form.Label>
              <Form.Control
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Role</Form.Label>
              <Form.Select
                value={userForm.role}
                onChange={(e) => setUserForm((f) => ({ ...f, role: e.target.value }))}
              >
                <option value="user">User</option>
                <option value="admin">Admin (store-level)</option>
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUserModal(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={createUser}
            disabled={!userForm.username.trim() || !userForm.email.trim() || userForm.password.length < 6}
          >
            Create User
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default SuperAdmin;
